import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { CatalogSearchResult } from '../../../data/catalogSearch';
import { cn } from '../../../lib/utils';
import {
  catalogResultHref,
  DISCLOSURE_ANIM_CLASS,
  LISTBOX_ID,
  optionId,
} from '../../../lib/catalogSearch';
import { Icon } from '../../../components/Icon';
import { useCatalogSearch } from './useCatalogSearch';
import { CatalogSearchBody, CatalogSearchFooter } from './CatalogSearchBody';
import { Kbd } from './CatalogSearchGuides';
import { m } from '../../../paraglide/messages';

export interface CatalogSearchProps {
  /** closes the palette (Esc, or after a selection) */
  onClose: () => void;
  /**
   * Overrides the default select behavior. By default selecting a result
   * navigates to its platform detail page; a playground mount can pass this to
   * load the result into a workbench instead. Receives the chosen result.
   */
  onSelect?: (result: CatalogSearchResult) => void;
}

/**
 * The catalog command palette body: one input with two modes. A short keyword
 * filters the loaded catalog instantly (no spinner); a full sentence or Return
 * runs the gateway's use-case semantic search and shows ranked endpoints with a
 * relevance score and, on the top results, the output fields that matter. Mounted
 * inside a dialog by CatalogSearchProvider; also usable standalone.
 */
export const CatalogSearch = ({ onClose, onSelect }: CatalogSearchProps) => {
  const s = useCatalogSearch();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const select = (result: CatalogSearchResult) => {
    if (onSelect) onSelect(result);
    else navigate({ to: catalogResultHref(result.slug) as never });
    onClose();
  };

  const onEnter = () => {
    // Return always opens the selected row (navigate, or the onSelect override).
    // The use-case search is now run by clicking the switch affordance, not Return.
    const row = s.rows[s.sel];
    if (row) select(row);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      s.moveSel(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      s.moveSel(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onEnter();
    }
  };

  const modeChip =
    s.phase === 'searching' || s.phase === 'results' || s.phase === 'error';

  return (
    <div className="flex max-h-[76vh] flex-col">
      <div className="flex h-13.5 shrink-0 items-center gap-3 border-b border-line-2 px-4">
        <Icon name="search" size={17} className="shrink-0 text-text-3" />
        <input
          ref={inputRef}
          value={s.query}
          onChange={(e) => s.setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={m.search_input_placeholder()}
          aria-label={m.search_input_aria()}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={s.rows.length > 0}
          aria-controls={LISTBOX_ID}
          aria-activedescendant={s.rows[s.sel] ? optionId(s.rows[s.sel].slug) : undefined}
          className="min-w-0 flex-1 bg-transparent text-15 tracking-[-0.1px] text-text placeholder:text-text-3 focus:outline-none"
        />
        {modeChip ? (
          <span className="mono rounded-6 border border-accent-line bg-accent-soft px-2 py-0.5 text-11 font-medium whitespace-nowrap text-accent">
            {m.search_mode_use_case()}
          </span>
        ) : null}
        <span className="hidden sm:inline-flex">
          <Kbd>esc</Kbd>
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={m.search_close_aria()}
          className="grid size-10 shrink-0 -mr-1 place-items-center rounded-8 text-text-3 hover:bg-surface-2 hover:text-text sm:hidden"
        >
          <Icon name="x" size={18} />
        </button>
      </div>

      <div
        className={cn(DISCLOSURE_ANIM_CLASS, 'min-h-0 flex-1')}
        style={{ gridTemplateRows: s.phase === 'empty' ? '0fr' : '1fr' }}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="h-full overflow-y-auto py-1">
            <CatalogSearchBody state={s} onRun={() => s.runSearch()} onSelect={select} />
          </div>
        </div>
      </div>

      {s.phase !== 'empty' ? (
        <CatalogSearchFooter phase={s.phase} count={s.catalogCount} total={s.total} />
      ) : null}
    </div>
  );
};
